import React from 'react';
import { ArticleEditor } from '../ui/ArticleEditor';
import { useStrategicPillar, useCreateStrategicPillar, useUpdateStrategicPillar } from '../../lib/api/hooks/strategic-pillar';
import { StrategicPillarFormSchema } from '../../lib/api/types';
import { showToast } from '../../lib/toast';

interface StrategicPillarEditorProps {
  pillarId?: number | string;
  onBack?: () => void;
  onSuccess?: () => void;
}

const StrategicPillarEditor: React.FC<StrategicPillarEditorProps> = ({ pillarId, onBack, onSuccess }) => {
  const { data: pillarData, isLoading: loadingPillar } = useStrategicPillar(
    String(pillarId),
  );

  const { mutate: createPillar, isPending: isCreating } = useCreateStrategicPillar();
  const { mutate: updatePillar, isPending: isUpdating } = useUpdateStrategicPillar();

  const isEditMode = !!pillarId;
  const isSaving = isCreating || isUpdating;

  // Define highlight options specific to Strategic Pillars
  const highlightOptions = [
    { key: 'landingPagePrioritiesOfAfsa', label: 'Landing page/Priorities of AfSA' },
    { key: 'whoWeAre', label: 'Who We Are' },
    { key: 'countries', label: 'Countries' },
    { key: 'history', label: 'History' },
    { key: 'member', label: 'Member' }
  ];

  const handleSubmit = (data: any, saveStatus: 'DRAFT' | 'PUBLISH') => {
    // Validate cover image
    if (!data.coverImage) {
      showToast.error('Please upload a cover image');
      return;
    }

    // Convert articleTag to array
    const tagsArray = typeof data.articleTag === 'string'
      ? data.articleTag.split(',').map((t: string) => t.trim()).filter(Boolean)
      : Array.isArray(data.articleTag)
      ? data.articleTag
      : [];

    const formData = {
      title: data.title,
      description: data.description || '',
      content: data.content,
      coverImageTitle: data.coverImageTitle || '',
      articleTag: tagsArray,
      landingPagePrioritiesOfAfsa: data.landingPagePrioritiesOfAfsa,
      whoWeAre: data.whoWeAre,
      countries: data.countries,
      history: data.history,
      member: data.member,
      status: saveStatus,
      coverImageUrl: data.coverImage
    };

    if (isEditMode) {
      updatePillar(
        { strategicPillarId: pillarId as string, data: formData },
        {
          onSuccess: () => {
            showToast.success(
              saveStatus === 'PUBLISH'
                ? 'Strategic pillar published successfully!'
                : 'Strategic pillar updated successfully!'
            );
            onSuccess?.();
          },
          onError: (error) => {
            showToast.error('Failed to update: ' + (error.message || 'Unknown error'));
          }
        }
      );
    } else {
      createPillar(formData, {
        onSuccess: () => {
          showToast.success(
            saveStatus === 'PUBLISH'
              ? 'Strategic pillar published successfully!'
              : 'Strategic pillar created as draft!'
          );
          onSuccess?.();
        },
        onError: (error) => {
          showToast.error('Failed to create: ' + (error.message || 'Unknown error'));
        }
      });
    }
  };

  return (
    <ArticleEditor
      formSchema={StrategicPillarFormSchema}
      articleId={pillarId}
      articleData={pillarData?.data}
      isLoading={loadingPillar}
      onBack={onBack}
      onSubmit={handleSubmit}
      highlightOptions={highlightOptions}
      isSaving={isSaving}
    />
  );
};

export default StrategicPillarEditor;
